import { useEffect } from 'react';
import { GridWidget } from '../types';
import { useUserStrategies } from '../../../context/UserStrategies';
import { Legend } from '../../Legend/Legend';
import PriceChangesLegend from './ChangesLegend';
import { RankedDataWrapper } from './RankedDataWrapper';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  PRICES_VARIATIONS_BY_INTERVAL,
  VOLUMES_VARIATIONS_BY_INTERVAL,
} from '../../../http/apollo-queries';
import { useIntervals } from '../../../hooks/useIntervals';
import { useErrorHandler } from 'react-error-boundary';
import { logger } from '../../../helpers/logger';
import { TDateISODate } from '../../../types/date';
import { IAsset, IStrategyAsset } from '../../../types/model';

export type TVariationsByInterval = {
  daily: {
    best: TAssetVariation[];
    worst: TAssetVariation[];
  };
  weekly: {
    best: TAssetVariation[];
    worst: TAssetVariation[];
  };
  monthly: {
    best: TAssetVariation[];
    worst: TAssetVariation[];
  };
};

export type TAssetVariation = {
  assetId: string;
  variation: number;
  startDate: TDateISODate;
  endDate: TDateISODate;
  startValue: number;
  endValue: number;
};

export type RankedAsset = IAsset & {
  percentage: number;
  startDate: TDateISODate;
  endDate: TDateISODate;
};

export const Changes = ({
  setLegend,
  target,
  width,
  setLastWidgetUpdateAt,
}: GridWidget & {
  target: 'PriceChanges' | 'VolumeChanges';
}) => {
  const { id: strategyId } = useParams();
  const onError = useErrorHandler();

  // Hooks
  const { strategies } = useUserStrategies();
  const { interval, IntervalSelector } = useIntervals(
    ['VAR_LAST', 'VAR_7D', 'VAR_30D'],
    'VAR_LAST'
  );

  const {
    strategyAssets: { items: strategyAssets },
  } = strategies.find((str) => str.id === strategyId)!;

  const {
    data: {
      getVolumeVariations: {
        daily: {
          best: dailyVolumeBest = [],
          worst: dailyVolumeWorst = [],
        } = {},
        weekly: {
          best: weeklyVolumeBest = [],
          worst: weeklyVolumeWorst = [],
        } = {},
        monthly: {
          best: monthlyVolumeBest = [],
          worst: monthlyVolumeWorst = [],
        } = {},
      } = {},
    } = {},
  } = useQuery<{
    getVolumeVariations: TVariationsByInterval;
  }>(VOLUMES_VARIATIONS_BY_INTERVAL, {
    skip: target === 'PriceChanges' || !strategyId,
    variables: { strategyId },
    pollInterval: 60000,
    fetchPolicy: 'cache-and-network',
    onError,
    onCompleted: () => setLastWidgetUpdateAt?.(new Date()),
  });

  const {
    data: {
      getPriceVariations: {
        daily: { best: dailyPriceBest = [], worst: dailyPriceWorst = [] } = {},
        weekly: {
          best: weeklyPriceBest = [],
          worst: weeklyPriceWorst = [],
        } = {},
        monthly: {
          best: monthlyPriceBest = [],
          worst: monthlyPriceWorst = [],
        } = {},
      } = {},
    } = {},
  } = useQuery<{
    getPriceVariations: TVariationsByInterval;
  }>(PRICES_VARIATIONS_BY_INTERVAL, {
    skip: target === 'VolumeChanges' || !strategyId,
    variables: { strategyId },
    pollInterval: 60000,
    fetchPolicy: 'cache-and-network',
    onError,
    onCompleted: () => setLastWidgetUpdateAt?.(new Date()),
  });

  const volumeVariations = (
    interval === 'VAR_LAST'
      ? [...dailyVolumeWorst, ...dailyVolumeBest]
      : interval === 'VAR_7D'
      ? [...weeklyVolumeWorst, ...weeklyVolumeBest]
      : interval === 'VAR_30D'
      ? [...monthlyVolumeWorst, ...monthlyVolumeBest]
      : []
  ).reduce(
    (obj, assetVariation) => ({
      ...obj,
      [assetVariation.assetId]: assetVariation,
    }),
    {} as { [key: string]: TAssetVariation }
  );

  const priceVariations = (
    interval === 'VAR_LAST'
      ? [...dailyPriceWorst, ...dailyPriceBest]
      : interval === 'VAR_7D'
      ? [...weeklyPriceWorst, ...weeklyPriceBest]
      : interval === 'VAR_30D'
      ? [...monthlyPriceWorst, ...monthlyPriceBest]
      : []
  ).reduce(
    (obj, assetVariation) => ({
      ...obj,
      [assetVariation.assetId]: assetVariation,
    }),
    {} as { [key: string]: TAssetVariation }
  );

  // Handlers
  // function onTabChange() {
  //   console.log('tab changed');
  // }

  // Effects
  useEffect(() => {
    setLegend?.(
      <Legend
        className="mr-8"
        CustomLegend={PriceChangesLegend}
        collapsed={width === 1}
      />
    );
  }, [setLegend, width]);

  const prepareChartData = (
    strategyAssets: IStrategyAsset[],
    variationsValues: { [x: string]: TAssetVariation }
  ) => {
    return strategyAssets
      .reduce(
        (arr, { asset, assetId }) =>
          variationsValues[assetId]
            ? [
                ...arr,
                {
                  ...asset,
                  percentage: variationsValues[assetId].variation,
                  startDate: variationsValues[assetId].startDate,
                  endDate: variationsValues[assetId].endDate,
                  startValue: variationsValues[assetId].startValue,
                  endValue: variationsValues[assetId].endValue,
                },
              ]
            : arr,
        [] as RankedAsset[]
      )
      .sort((a, b) => b.percentage - a.percentage);
  };

  const chartData = prepareChartData(
    strategyAssets,
    target === 'PriceChanges' ? priceVariations : volumeVariations
  );

  logger.log(
    'WidgetChange',
    target,
    target === 'PriceChanges' ? priceVariations : volumeVariations,
    chartData
  );

  return (
    <div className="overflow-hidden flex flex-col h-full">
      <div className="flex justify-between h-16 border-b border-grey-10 dark:border-grey-80">
        {/* <Tabs
          rowList={ROW_LIST}
          onTabChange={onTabChange}
          collapsed={width === 1}
        /> */}
        <IntervalSelector className="border-l border-grey-10 dark:border-grey-80 ml-auto" />
      </div>
      {/* CONTENT */}
      <div className="overflow-hidden flex flex-grow-1">
        <RankedDataWrapper
          top={chartData
            .filter(({ percentage }) => percentage >= 0)
            .slice(0, 10)}
          worst={chartData
            .filter(({ percentage }) => percentage < 0)
            .slice(-10)
            .reverse()}
          width={width}
        />
      </div>
    </div>
  );
};
